import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import { useMaterialUIController } from "context";
import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { getOrder, postOrder, putOrder } from "../../../api/orders";

import GeneralInfosOrder from "./GeneralInfosOrder";
import ResponsibleOrder from "./ResponsibleOrder";

import PropTypes from "prop-types";

import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";
import { useDispatch, useSelector } from "react-redux";

import { areaColor } from "../../../config/enum";

import { useNavigate } from "react-router-dom";

import { v4 as uuidv4 } from "uuid";

export default function CreateEditOrder({ edit }) {
  const [open, setOpen] = useState(false);

  const [orderId, setOrderId] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [numberAff, setNumberAff] = useState("");
  const [numberDoli, setNumberDoli] = useState("");
  const [workType, setWorkType] = useState("");
  const [siteName, setSiteName] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");

  const [responsibleBusiness, setResponsibleBusiness] = useState(null);
  const [foreman, setForeman] = useState(null);
  const [salesman, setSalesman] = useState(null);
  const [managementAssistant, setManagementAssistant] = useState(null);
  const [projectManager, setProjectManager] = useState("");
  const [buildingOwner, setBuildingOwner] = useState("");
  const [areaHourlyRates, setAreaHourlyRates] = useState("");

  const regexFull = /AF{2}\d+\s\+\sCO\d{4}-\d{4}$/i;
  const regexNoAff = /CO\d{4}-\d{4}$/i;
  const regexNoDoli = /AF{2}\d+$/i;

  const currentOrder = useSelector((state) => state.orders.currentOrder);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (edit && currentOrder) {
      setOrderId(currentOrder.order_id);
      setOrderNumber(currentOrder.order_number);
      if (
        regexFull.test(currentOrder.order_number) ||
        regexNoDoli.test(currentOrder.order_number) ||
        regexNoAff.test(currentOrder.order_number)
      ) {
        if (currentOrder.order_number.indexOf("AFF") !== -1) {
          setNumberAff(currentOrder.order_number.match(/AFF(\d+)/)[1]);
        }
        if (currentOrder.order_number.indexOf("CO") !== -1) {
          setNumberDoli(currentOrder.order_number.slice(-11));
        }
      }
      setWorkType(currentOrder.work_type);
      setSiteName(currentOrder.site_name);
      setArea(currentOrder.area);
      setCity(currentOrder.city);
      setResponsibleBusiness(
        currentOrder.responsible_business_id !== null
          ? {
              label: currentOrder.responsible_business_name,
              id: currentOrder.responsible_business_id,
            }
          : null
      );
      setForeman(
        currentOrder.foreman_id !== null
          ? {
              label: currentOrder.foreman_name,
              id: currentOrder.foreman_id,
            }
          : null
      );
      setSalesman(
        currentOrder.salesman_id !== null
          ? {
              label: currentOrder.salesman_name,
              id: currentOrder.salesman_id,
            }
          : null
      );
      setManagementAssistant(
        currentOrder.management_assistant_id !== null
          ? {
              label: currentOrder.management_assistant_name,
              id: currentOrder.management_assistant_id,
            }
          : null
      );
      setProjectManager(currentOrder.project_manager);
      setBuildingOwner(currentOrder.building_owner);
      setAreaHourlyRates(currentOrder.area_hourly_rates);
    } else if (!edit) {
      setOrderId(uuidv4());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, currentOrder]);

  async function createOrderAPI() {
    const order = {
      order_id: orderId,
      order_number:
        numberAff && numberDoli
          ? `AFF${String(numberAff).padStart(8, "0")} + ${numberDoli}`
          : !numberAff && numberDoli
          ? `${numberDoli}`
          : `AFF${String(numberAff).padStart(8, "0")}`,
      work_type: workType,
      site_name: siteName,
      area: area,
      city: city,
      responsible_business: responsibleBusiness ? responsibleBusiness.id : null,
      foreman: foreman ? foreman.id : null,
      salesman: salesman ? salesman.id : null,
      management_assistant: managementAssistant ? managementAssistant.id : null,
      project_manager: projectManager,
      building_owner: buildingOwner,
      area_hourly_rates: areaHourlyRates,
    };
    await postOrder(order);
  }

  async function updateOrderAPI() {
    const order = {
      order_number:
        numberAff && numberDoli
          ? `AFF${String(numberAff).padStart(8, "0")} + ${numberDoli}`
          : !numberAff && numberDoli
          ? `${numberDoli}`
          : `AFF${String(numberAff).padStart(8, "0")}`,
      work_type: workType,
      site_name: siteName,
      area: area,
      city: city,
      responsible_business: responsibleBusiness ? responsibleBusiness.id : null,
      foreman: foreman ? foreman.id : null,
      salesman: salesman ? salesman.id : null,
      management_assistant: managementAssistant ? managementAssistant.id : null,
      project_manager: projectManager,
      building_owner: buildingOwner,
      area_hourly_rates: areaHourlyRates,
    };
    await putOrder(order, orderId);
  }

  function resetData() {
    setOrderNumber("");
    setNumberAff("");
    setNumberDoli("");
    setWorkType("");
    setSiteName("");
    setArea("");
    setResponsibleBusiness("");
    setForeman("");
    setSalesman("");
    setManagementAssistant("");
    setProjectManager("");
    setBuildingOwner("");
    setAreaHourlyRates("");
  }

  async function fetchOrder() {
    const response = await getOrder(orderId);
    dispatch(setCurrentOrder(response.data));
  }

  async function cancel() {
    if (edit) {
      await fetchOrder();
    } else {
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  async function submit() {
    if (edit) {
      await updateOrderAPI();
      await fetchOrder();
    } else {
      await createOrderAPI();
      navigate(`/commandes/${orderId}`);
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  const [step, setStep] = useState(0);
  const steps = ["Informations Générales", "Personnes liées"];
  const isLastStep = step === steps.length - 1;

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <GeneralInfosOrder
            numberAff={numberAff}
            setNumberAff={setNumberAff}
            numberDoli={numberDoli}
            setNumberDoli={setNumberDoli}
            workType={workType}
            setWorkType={setWorkType}
            siteName={siteName}
            setSiteName={setSiteName}
            area={area}
            setArea={setArea}
            city={city}
            setCity={setCity}
            areaHourlyRates={areaHourlyRates}
            setAreaHourlyRates={setAreaHourlyRates}
            edit={edit}
          />
        );
      case 1:
        return (
          <ResponsibleOrder
            responsibleBusiness={responsibleBusiness}
            setResponsibleBusiness={setResponsibleBusiness}
            foreman={foreman}
            setForeman={setForeman}
            salesman={salesman}
            setSalesman={setSalesman}
            projectManager={projectManager}
            managementAssistant={managementAssistant}
            setManagementAssistant={setManagementAssistant}
            setProjectManager={setProjectManager}
            buildingOwner={buildingOwner}
            setBuildingOwner={setBuildingOwner}
          />
        );
      default:
        return null;
    }
  }

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <MDButton
          variant="contained"
          sx={{
            height: 53.13,
            width: "100%",
            color: !darkMode ? "white" : "#1A2035",
            boxShadow:
              darkMode &&
              "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
            "&:hover": {
              boxShadow:
                darkMode &&
                "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
            },
          }}
          color={darkMode ? "white" : "dark"}
          size="large"
          onClick={() => setOpen(true)}
        >
          Ajouter une commande
        </MDButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={-12}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: edit
                      ? currentOrder && areaColor[currentOrder.area]
                      : darkMode
                      ? "white"
                      : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: edit
                      ? `0 1px 6px 1px ${currentOrder && areaColor[currentOrder.area]}`
                      : darkMode
                      ? "0 1px 6px 1px white"
                      : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      {edit ? (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode && !edit ? "inherit" : "white"}
                        >
                          Modification de la commande
                        </MDTypography>
                      ) : (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode && !edit ? "inherit" : "white"}
                        >
                          Ajouter une nouvelle commande
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode && !edit ? "inherit" : "white"}
                    >
                      Veuillez rentrer les informations relatives à la commande
                    </MDTypography>
                  </MDBox>
                  <Stepper
                    sx={{ boxShadow: "none", backgroundImage: "none" }}
                    activeStep={step}
                    alternativeLabel
                  >
                    {steps.map((label) => (
                      <Step
                        sx={{
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                            {
                              color: darkMode && !edit && "#344767 !important",
                            },
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                            {
                              color: darkMode && !edit && "#344767 !important",
                            },
                        }}
                        key={label}
                      >
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                              color: darkMode && !edit && "#344767 !important",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                              color: darkMode && !edit && "#344767 !important",
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                              background: darkMode && !edit && "#344767",
                              fill: darkMode && !edit && "#344767",
                              stroke: darkMode && !edit && "#344767",
                              borderColor: darkMode && !edit && "#344767",
                              boxShadow: darkMode && !edit && "0rem 0rem 0rem 0.125rem #344767",
                            },
                            "& .MuiStepIcon-root.Mui-completed": {
                              background: darkMode && !edit && "#344767",
                              fill: darkMode && !edit && "#344767",
                              stroke: darkMode && !edit && "#344767",
                              borderColor: darkMode && !edit && "#344767",
                              boxShadow: darkMode && !edit && "0rem 0rem 0rem 0.125rem #344767",
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {getStepContent(step)}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {step === 0 ? (
                        <MDBox />
                      ) : (
                        <MDButton variant="gradient" color="warning" onClick={previous}>
                          Précédent
                        </MDButton>
                      )}
                      <MDBox textAlign="right">
                        <Grid container spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              disabled={
                                !(numberAff.length >= 1 || numberAff === "") ||
                                !(/CO\d{4}-\d{4}/i.test(numberDoli) || numberDoli === "") ||
                                !workType ||
                                !area
                              }
                              variant="gradient"
                              color="success"
                              onClick={!isLastStep ? next : submit}
                            >
                              {isLastStep ? "Envoyer" : "Suivant"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditOrder.propTypes = {
  edit: PropTypes.bool.isRequired,
};
